import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import { send } from '@actual-app/core/platform/client/connection';
import { comprobarReglasContrasena } from '@actual-app/core/shared/contrasena';

import { Boton } from '#components/mobile/ui/Boton';
import { Icono } from '#components/mobile/ui/Icono';
import { color, texto } from '#components/mobile/ui/tokens';

import { CampoContrasena } from './CampoContrasena';

type NuevaContrasenaProps = {
  textoBoton: string;
  /** Devuelve un mensaje de error para enseñarlo, o null si fue bien. */
  onEnviar: (contrasena: string) => Promise<string | null>;
};

/**
 * Contraseña nueva escrita dos veces, con las reglas a la vista (8 o más,
 * letras y números) y la comprobación de listas de contraseñas robadas.
 */
export function NuevaContrasena({
  textoBoton,
  onEnviar,
}: NuevaContrasenaProps) {
  const { t } = useTranslation();
  const [contrasena, setContrasena] = useState('');
  const [repetida, setRepetida] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const reglas = comprobarReglasContrasena(contrasena);
  const cumple = reglas.largo && reglas.letras && reglas.numeros;
  const coinciden = repetida.length > 0 && repetida === contrasena;
  const noCoinciden =
    repetida.length > 0 &&
    (repetida.length >= contrasena.length ||
      !contrasena.startsWith(repetida)) &&
    repetida !== contrasena;

  async function enviar() {
    if (!cumple || !coinciden || enviando) {
      return;
    }
    setEnviando(true);
    setError(null);
    try {
      const { filtrada } = await send('cofre-contrasena-filtrada', {
        contrasena,
      });
      if (filtrada) {
        setError(
          t(
            'That password appears in lists of stolen passwords. Choose another one.',
          ),
        );
        return;
      }
      setError(await onEnviar(contrasena));
    } finally {
      setEnviando(false);
    }
  }

  const lista = [
    { hecho: reglas.largo, texto: t('8 characters or more') },
    { hecho: reglas.letras && reglas.numeros, texto: t('Letters and numbers') },
  ];

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        void enviar();
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 18 }}
    >
      <CampoContrasena
        etiqueta={t('Rumbo password')}
        valor={contrasena}
        onCambio={setContrasena}
        estado={cumple ? 'bien' : undefined}
        autoComplete="new-password"
        autoFocus
      >
        <ul
          style={{
            margin: 0,
            padding: 0,
            listStyle: 'none',
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
          }}
        >
          {lista.map(regla => (
            <li
              key={regla.texto}
              style={{
                ...texto.secundario,
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                color: regla.hecho ? color.fg : color.fg3,
              }}
            >
              <span
                style={{
                  width: 16,
                  display: 'flex',
                  color: regla.hecho ? color.accent : color.fg4,
                }}
              >
                {regla.hecho ? (
                  <Icono nombre="check" size={16} strokeWidth={2.6} />
                ) : (
                  <span
                    aria-hidden
                    style={{
                      width: 6,
                      height: 6,
                      margin: 5,
                      borderRadius: 3,
                      backgroundColor: 'currentColor',
                    }}
                  />
                )}
              </span>
              {regla.texto}
            </li>
          ))}
        </ul>
      </CampoContrasena>

      <CampoContrasena
        etiqueta={t('Repeat the password')}
        valor={repetida}
        onCambio={setRepetida}
        estado={coinciden ? 'bien' : noCoinciden ? 'error' : undefined}
        autoComplete="new-password"
      >
        {coinciden && (
          <Text style={{ ...texto.secundario, color: color.accent }}>
            <Trans>They match</Trans>
          </Text>
        )}
        {noCoinciden && (
          <Text role="alert" style={{ ...texto.secundario, color: color.bad }}>
            {t("They don't match. Check both (the eye lets you see them).")}
          </Text>
        )}
      </CampoContrasena>

      {error && (
        <Text role="alert" style={{ ...texto.cuerpo, color: color.bad }}>
          {error}
        </Text>
      )}

      <View style={{ marginTop: 4 }}>
        <Boton
          bloque
          type="submit"
          isDisabled={!cumple || !coinciden || enviando}
        >
          {textoBoton}
        </Boton>
      </View>
    </form>
  );
}
